import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { CARET_SMALL } from '@/components/switchback/Icon/assets';
import Icon from '@/components/switchback/Icon/IconComponent';
import { usePositionFixedOnScroll } from '@/hooks/usePositionFixedOnScroll';
import { hasPressed } from '@/utility/accessibility';

import css from './GalleryOverlay.module.css';

interface IGalleryOverlay {
  headerComponent?: React.ReactNode;
  categoriesComponent?: React.ReactNode;
  goToListing: () => void;
}

const GalleryOverlay: React.FC<React.PropsWithChildren<IGalleryOverlay>> = ({
  headerComponent,
  categoriesComponent,
  goToListing,
  children,
}) => {
  const intl = useIntl();
  const { position } = usePositionFixedOnScroll('gallery-header');

  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (hasPressed(e).ESCAPE) {
        goToListing();
      }
    },
    [goToListing],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <>
      <div className="grid grid-cols-main container-main gap-x-default">
        <div className="relative min-w-full gap-x-default col-start-0 col-span-16 lg:col-start-0 lg:col-span-18">
          <button
            className={css.closeButton}
            aria-label={intl.formatMessage({ defaultMessage: 'Close gallery', id: '/q2ubh' })}
            data-testid="gallery-overlay-close-btn"
            onClick={goToListing}
            style={{ position }}>
            <Icon name={CARET_SMALL} />
          </button>
        </div>
      </div>
      <header className={css.header} id="gallery-header" style={{ position }}>
        {headerComponent}
      </header>
      <div className="grid grid-cols-main container-main gap-x-default">
        <section className="relative min-w-full gap-x-default col-start-0 col-span-16 lg:col-start-0 lg:col-span-18">
          <div className={`${css.content} md:gap-x-default md:grid-cols-main`}>
            <aside className={css.categories}>{categoriesComponent}</aside>
            <main className={css.photos}>{children}</main>
          </div>
        </section>
      </div>
    </>
  );
};

export default GalleryOverlay;
